import axios from "axios";

// Resolve the backend base URL with a safe fallback. In production, if the
// build was compiled without REACT_APP_BACKEND_URL baked in, `process.env.*`
// is `undefined` and template-literal interpolation produces the string
// "undefined/api/..." — which then hits Apache's SPA fallback and returns
// index.html (text/html). The empty-string fallback makes every request a
// relative URL against the current origin (e.g. carrentapp.brainart.gr/api),
// which is the correct behaviour when the frontend and backend are served
// from the same domain.
const RAW = process.env.REACT_APP_BACKEND_URL;
const IS_UNCONFIGURED = !(typeof RAW === "string" && RAW.trim() && RAW !== "undefined");
const BACKEND_URL = IS_UNCONFIGURED
  ? ""                          // relative to current origin (same-origin deploy)
  : RAW.replace(/\/+$/, "");    // strip trailing slashes
export const API = `${BACKEND_URL}/api`;

// Flag consumed by <BackendUrlBanner> to render a visible red banner when the
// deploy is missing REACT_APP_BACKEND_URL. Also emit one loud console warning
// at boot so ops sees this in the browser devtools within seconds of a bad
// deploy.
export const BACKEND_URL_UNCONFIGURED = IS_UNCONFIGURED;
if (IS_UNCONFIGURED && typeof console !== "undefined") {
  console.warn(
    "[Brainart CT | Car Rental App] REACT_APP_BACKEND_URL is not defined in this build. " +
    "Falling back to relative /api URLs (assumes same-origin backend). " +
    "If /api routes 404 with HTML, rebuild the frontend with the env var set.",
  );
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  responseType: "json",
  // Force JSON parsing; if the body is HTML (e.g. a proxy 404/500 page or a
  // wildcard SPA fallback) JSON.parse throws → axios rejects with an error
  // and the response never enters application state as a string/DOM blob.
  transformResponse: [(raw) => {
    if (raw == null || raw === "") return null;
    if (typeof raw !== "string") return raw;
    try {
      return JSON.parse(raw);
    } catch {
      // Non-JSON payload (HTML error page, plain text). Surface as an error
      // by returning a sentinel that downstream code can detect.
      return { __nonJson: true, raw: raw.slice(0, 200) };
    }
  }],
});

// Reject any response whose payload is not a plain JSON object/array. This
// catches leaked HTML fallbacks so they can never contaminate React state
// (e.g. AuthContext's `user`), which historically caused RBAC misrouting.
api.interceptors.response.use(
  (res) => {
    const ct = (res.headers?.["content-type"] || "").toLowerCase();
    if (ct.includes("text/html")) {
      return Promise.reject(
        Object.assign(new Error("Server returned HTML instead of JSON"), {
          response: { ...res, data: { detail: "Non-JSON response" } },
        }),
      );
    }
    if (res.data && typeof res.data === "object" && res.data.__nonJson) {
      return Promise.reject(
        Object.assign(new Error("Malformed JSON response"), {
          response: { ...res, data: { detail: "Malformed JSON response" } },
        }),
      );
    }
    return res;
  },
  (err) => {
    // Cloudflare / K8s ingress gateway errors (backend down / restarting)
    // arrive as text/html 502-504. Normalise them into a stable JSON-shaped
    // error object so downstream callers get a clean, actionable message
    // instead of "Non-JSON response".
    const res = err?.response;
    const ct = (res?.headers?.["content-type"] || "").toLowerCase();
    const status = res?.status;
    if (res && (ct.includes("text/html") || typeof res.data === "string")) {
      const gateway = status >= 502 && status <= 504;
      const detail = gateway
        ? "The server is temporarily unreachable. Please retry in a moment."
        : `Server returned an unexpected non-JSON response (HTTP ${status}).`;
      err.response = { ...res, data: { detail, status, gateway } };
    }
    return Promise.reject(err);
  },
);

export default api;

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Strict guard: only accept plain user profile objects. Rejects arrays, DOM
// elements (React synthetic events, HTMLElement), strings, and anything
// missing the auth-critical fields. Used by AuthContext to sanitise any
// value before it becomes `user` state.
export function sanitizeUser(candidate) {
  if (candidate == null) return null;
  if (typeof candidate !== "object") return null;
  if (Array.isArray(candidate)) return null;
  // Reject DOM nodes / React elements (both expose nodeType or $$typeof).
  if (typeof window !== "undefined" && candidate instanceof window.Node) return null;
  if (typeof candidate.nodeType === "number") return null;
  if (candidate.$$typeof) return null;

  const { id, email, role, name } = candidate;
  if (typeof id !== "string" || !id) return null;
  if (typeof email !== "string" || !email) return null;
  if (typeof role !== "string" || !role) return null;

  // Return a shallow-cloned, allow-listed copy so no exotic prototype or
  // extra reference leaks into React state.
  return {
    id,
    email,
    role,
    name: typeof name === "string" ? name : "",
    token: typeof candidate.token === "string" ? candidate.token : undefined,
    acl: candidate.acl && typeof candidate.acl === "object" && !Array.isArray(candidate.acl)
      ? candidate.acl
      : undefined,
    created_at: typeof candidate.created_at === "string" ? candidate.created_at : undefined,
  };
}
